import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyGetNotificationsQuery } from "../../features/notification/notificationApiSlice";
import { apiSlice } from "../../features/api/apiSlice";
import { SOCKET_URL } from "../../config/axios";
import { toast } from "react-toastify";
import { selectCurrentToken } from "../../store/slices/authSlice";

export const useNavbarRealtime = ({ userId }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const token =
    useSelector(selectCurrentToken) || localStorage.getItem("token");
  const [getNotifications] = useLazyGetNotificationsQuery();
  const [notifications, setNotifications] = useState([]);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const isMessagesPageRef = useRef(location.pathname === "/messages");

  useEffect(() => {
    isMessagesPageRef.current = location.pathname === "/messages";
  }, [location.pathname]);

  useEffect(() => {
    const handleOpenMessenger = (event) => {
      setUnreadMsgCount(0);
      localStorage.removeItem("messengerTargetUser");
      navigate("/messages", {
        state: {
          initialUser: event.detail || null,
          returnTo: location.pathname,
        },
      });
    };
    window.addEventListener("open-messenger", handleOpenMessenger);
    return () => window.removeEventListener("open-messenger", handleOpenMessenger);
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!userId) return;

    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080/api";
    const url = `${API_BASE}/message/unread-stream?userId=${userId}`;
    const eventSource = new EventSource(url);

    eventSource.addEventListener("unread_count", (event) => {
      try {
        const { count } = JSON.parse(event.data);
        setUnreadMsgCount(count);
      } catch (_) {}
    });

    eventSource.onerror = () => {
      console.warn("[SSE] unread-stream connection error.");
    };

    return () => eventSource.close();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const fetchNotifications = async () => {
      try {
        const response = await getNotifications({
          userId,
          limit: 50,
          skip: 0,
        }).unwrap();
        if (response.success) {
          setNotifications((current) => {
            const byId = new Map(
              [...current, ...response.data].map((notification) => [
                notification._id,
                notification,
              ]),
            );
            return [...byId.values()].sort(
              (first, second) =>
                new Date(second.createdAt) - new Date(first.createdAt),
            );
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchNotifications();
  }, [getNotifications, userId]);

  useEffect(() => {
    if (!userId || !token) return undefined;

    const API_BASE =
      process.env.REACT_APP_API_URL || "http://localhost:8080/api";
    const controller = new AbortController();
    let retryTimer;
    let disposed = false;

    const addNotification = (data) => {
      if (data?.type === "SHOP_ACCESS_UPDATED") {
        toast.info(data.message || "You have access based on shop access", {
          toastId: `shop-access-${data.meta?.shopId || Date.now()}`,
          position: "top-center",
          autoClose: 5000,
        });
        dispatch(
          apiSlice.util.invalidateTags([
            { type: "Shop", id: "MANAGED" },
            ...(data.meta?.shopId
              ? [{ type: "Shop", id: `MEMBERS-${data.meta.shopId}` }]
              : []),
          ]),
        );
        return;
      }

      setNotifications((previous) =>
        previous.some((notification) => notification._id === data._id)
          ? previous
          : [data, ...previous],
      );
      if (data.type === "SHOP_ACCESS_INVITATION") {
        toast.info(data.message, {
          toastId: `notification-${data._id}`,
          position: "top-center",
          autoClose: 6000,
          onClick: () => {
            if (data.meta?.membershipId) {
              navigate(
                data.meta.invitationPath ||
                  `/shop-access-invitation/${data.meta.membershipId}`,
              );
            }
          },
        });
      }
      if (
        data.type === "SHOP_ACCESS_ACCEPTED" ||
        data.type === "SHOP_ACCESS_REJECTED"
      ) {
        toast.info(data.message, {
          toastId: `notification-${data._id}`,
          position: "top-center",
          autoClose: 6000,
        });
      }
      if (
        data.type === "SHOP_ACCESS_ACCEPTED" &&
        data.meta?.shopId
      ) {
        dispatch(
          apiSlice.util.invalidateTags([
            { type: "Shop", id: `MEMBERS-${data.meta.shopId}` },
          ]),
        );
      }
      if (
        data.type === "CONNECTION_ACCEPTED" ||
        data.type === "CONNECTION_REQUEST"
      ) {
        dispatch(apiSlice.util.invalidateTags(["UserConnections"]));
      }
    };

    const connect = async () => {
      try {
        const response = await fetch(`${API_BASE}/notification/stream`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          throw new Error(`Notification stream failed (${response.status}).`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!disposed) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split(/\r?\n\r?\n/);
          buffer = events.pop() || "";

          events.forEach((event) => {
            const data = event
              .split(/\r?\n/)
              .find((line) => line.startsWith("data:"))
              ?.slice(5)
              .trim();
            if (data) addNotification(JSON.parse(data));
          });
        }
      } catch (error) {
        if (!disposed && error.name !== "AbortError") {
          console.error("[notification-stream] Connection failed:", error);
        }
      }

      if (!disposed) {
        retryTimer = window.setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
      controller.abort();
    };
  }, [dispatch, navigate, token, userId]);

  useEffect(() => {
    if (!userId) return undefined;
    let socket;
    let disposed = false;

    import("socket.io-client")
      .then(({ io }) => {
        if (disposed) return;
        socket = io(SOCKET_URL);
        socket.emit("join_user", userId);
        socket.on("connection_request_cancelled", () => {
          dispatch(apiSlice.util.invalidateTags(["UserConnections"]));
        });
        socket.on("connection_list_updated", () => {
          dispatch(apiSlice.util.invalidateTags(["UserConnections"]));
        });
        socket.on("receiveMessage", (message) => {
          const sentByMe =
            String(message.sender || message.sendershopid || "") ===
            String(userId);
          if (!sentByMe && !isMessagesPageRef.current) {
            setUnreadMsgCount((previous) => previous + 1);
          }
        });
        socket.on("messages_read", ({ readBy }) => {
          if (String(readBy) === String(userId)) setUnreadMsgCount(0);
        });
      })
      .catch((error) => {
        console.warn("[socket] failed to load socket.io-client", error);
      });

    return () => {
      disposed = true;
      if (socket) socket.disconnect();
    };
  }, [dispatch, userId]);

  return {
    notifications,
    setNotifications,
    setUnreadMsgCount,
    unreadMsgCount,
  };
};